import { MagicDoorFeature } from '~/swagger/portal/Api';

export type SettingsRouteChild = {
  labelKey: string;
  path: string;
  subtitleKey?: string;
  feature?: MagicDoorFeature;
  hiddenFromOverview?: boolean;
  /** Hidden from the overview unless the signed-in manager holds this permission. */
  permission?: { resource: string; action: string };
};

export type SettingsRouteGroup = {
  labelKey: string;
  children: SettingsRouteChild[];
};

export const settingsRouteGroups: SettingsRouteGroup[] = [
  {
    labelKey: 'Account',
    children: [
      {
        labelKey: 'Information',
        path: 'account/information',
        subtitleKey: 'Edit your account basic information',
      },
      {
        labelKey: 'Notifications',
        path: 'account/notifications',
        subtitleKey: 'Choose which notifications you receive',
      },
      {
        labelKey: 'Developer',
        path: 'account/developer',
        subtitleKey: 'Create and manage API keys',
        permission: { resource: 'api_keys', action: 'read' },
      },
      {
        labelKey: 'Account activities',
        path: 'account/activities',
        hiddenFromOverview: true,
      },
    ],
  },
  {
    labelKey: 'Company',
    children: [
      {
        labelKey: 'Company information',
        path: 'company/information',
        subtitleKey: 'Set up company information and working hours',
      },
      {
        labelKey: 'Business information',
        path: 'company/business',
        subtitleKey: 'Set up business information',
      },
      {
        labelKey: 'Payment accounts',
        path: 'company/payment-accounts',
        subtitleKey: 'Payment account and invoice settings',
      },
      {
        labelKey: 'Magic tags',
        path: 'company/magic-tags',
        subtitleKey: 'Custom tag settings',
      },
    ],
  },
  {
    labelKey: 'Property',
    children: [
      {
        labelKey: 'Property',
        path: 'property/general',
        subtitleKey: 'Property general',
      },
      {
        labelKey: 'Rent notifications',
        path: 'property/rent-notifications',
        subtitleKey: 'Notice on Rent Management Settings',
      },
      {
        labelKey: 'Tenant payment reminders',
        path: 'property/tenant-payment-reminders',
        subtitleKey: 'Automated payment reminder messages sent to tenants',
      },
      {
        labelKey: 'Rent payment',
        path: 'property/rent-payment',
        subtitleKey: 'Set up the rental payment method, etc...',
      },
      {
        labelKey: 'Deposit payment',
        path: 'property/deposit-payment',
        subtitleKey: 'Set up a deposit collection account, etc...',
      },
      {
        labelKey: 'Work order',
        path: 'property/work-order',
        subtitleKey: 'Work order sending message settings',
      },
      {
        labelKey: 'Rent payment processing fees',
        path: 'property/rent-payment-processing-fees',
        subtitleKey: 'Set rental payment processing fees',
      },
      {
        labelKey: 'Late fees',
        path: 'property/late-fees',
        subtitleKey: 'Set up late fee for charges',
      },
      {
        labelKey: 'Management fees',
        path: 'property/management-fees',
        subtitleKey: 'Management fees settings',
        feature: MagicDoorFeature.CompanyLedger,
      },
      {
        labelKey: 'Maintenance request',
        path: 'property/settings-maintenance',
        subtitleKey: 'Tenants to close maintenance requests, etc...',
      },
      {
        labelKey: 'Contributions',
        path: 'property/contributions',
        subtitleKey: 'Contributions - Basic Settings',
      },
    ],
  },
  {
    labelKey: 'Lease',
    children: [
      {
        labelKey: 'Syndication',
        path: 'lease/syndication',
        subtitleKey: 'Syndication Platforms',
      },
      {
        labelKey: 'Lease renewal',
        path: 'lease/renewal',
        subtitleKey: 'Lease templates etc...',
      },
      {
        labelKey: 'Automatic lease renewal',
        path: 'lease/automatic-lease-renewal',
        subtitleKey: 'Automatic lease renewal rule settings',
      },
      {
        labelKey: 'Auto-pay incentive',
        path: 'lease/auto-pay-incentive',
        subtitleKey: 'Auto-pay incentive settings',
      },
      {
        labelKey: 'Lease invoice',
        path: 'lease/lease-invoice',
        subtitleKey: 'Automatically send invoice settings',
      },
    ],
  },
  {
    labelKey: 'Rental Application',
    children: [
      {
        labelKey: 'Application fee',
        path: 'rental/applicationfee',
        subtitleKey: 'Application fees settings',
      },
      {
        labelKey: 'Applicant information',
        path: 'rental/information',
        subtitleKey: 'Rental application general settings',
      },
      {
        labelKey: 'Chart of account',
        path: 'rental/chart-of-account',
        subtitleKey: 'Chart of account settings',
      },
      {
        labelKey: 'General settings',
        path: 'rental/general-settings',
        subtitleKey: 'Rental application general settings',
      },
      {
        labelKey: 'Standard questions',
        path: 'rental/standard-questions',
        subtitleKey: 'Standard questions settings',
      },
    ],
  },
  {
    labelKey: 'Insurance',
    children: [
      {
        labelKey: 'Renters insurance',
        path: 'lease/renters-insurance',
        subtitleKey: 'Renters insurance enable reminders',
      },
      {
        labelKey: 'Damage Waivers',
        path: 'insurance/damage-waivers',
        subtitleKey: 'Damage waiver program settings',
      },
    ],
  },
  {
    labelKey: 'Portal management',
    children: [
      {
        labelKey: 'Portal invite & url',
        path: 'portal/invite-url',
        subtitleKey: 'Modify your URL',
      },
      {
        labelKey: 'Portal invite & text templates',
        path: 'portal/invite-text',
        subtitleKey: 'Customize the emails and text messages your tenants receive',
      },
      {
        labelKey: 'Portal settings',
        path: 'portal/portal-settings',
        subtitleKey: 'Style settings for the portal',
      },
      {
        labelKey: 'Tenant portal',
        path: 'portal/tenant-portal',
        hiddenFromOverview: true,
      },
      {
        labelKey: 'Preferences',
        path: 'preferences',
        hiddenFromOverview: true,
      },
    ],
  },
  {
    labelKey: 'Chat',
    children: [
      {
        labelKey: 'Chat',
        path: 'chats/chat',
        subtitleKey: 'Chat settings',
      },
      {
        labelKey: 'Text',
        path: 'chats/text',
        subtitleKey: 'Enable or disable text',
      },
      {
        labelKey: 'Email',
        path: 'chats/email',
        subtitleKey: 'Enable or disable emails',
      },
    ],
  },
];

export const settingsRouteChildren = settingsRouteGroups.flatMap((group) => group.children);

export const getSettingsRoutePath = (path: string) => {
  if (path.startsWith('/settings')) {
    return path;
  }

  return `/settings/${path.replace(/^\/+/, '')}`;
};

export const getSettingsChildRouteMetadata = (pathname: string) => {
  const normalizedPathname = pathname.toLowerCase();

  return settingsRouteChildren.find((route) => {
    const routePath = getSettingsRoutePath(route.path).toLowerCase();
    return normalizedPathname === routePath || normalizedPathname.startsWith(`${routePath}/`);
  });
};
